import { useEffect, useRef, useState, useMemo } from 'react';
import { Listbox, Combobox } from '@headlessui/react';
import {
	CheckIcon,
	ChevronUpDownIcon,
	PlusIcon,
} from '@heroicons/react/20/solid';
import { ClockIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import {
	updateWorkOrder,
	deleteWorkOrder,
	addWorkOrderAsset,
	removeWorkOrderAsset,
	addWorkOrderContact,
	removeWorkOrderContact,
} from '../api/workOrders';
import { getModuleCategoriesByModuleKey } from '../api/modules';
import { getTeam } from '../api/company';
import { getAssets } from '../api/assets';
import { getApprovedSops, getSopVersions } from '../api/sops';
import { getContacts } from '../api/contacts';
import { getContracts } from '../api/contracts';

import {
	StackableModal,
	DateTimePicker,
	AssetSelectorBeta,
	VendorSelectorBeta,
} from '../components/nextgen';
import { classNames, getUserFullName } from '../components/helpers/functions';

import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';

const MODAL_NAME = 'editWorkOrder';

/**
 * Extract the local date (YYYY-MM-DD) from a date string.
 * Handles both UTC ISO strings (ending with Z) and local date strings.
 */
const getLocalDateString = (dateString) => {
	if (!dateString) return '';
	// If it's a UTC ISO string, convert to local time first
	if (dateString.includes('T') && dateString.endsWith('Z')) {
		const date = new Date(dateString);
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}
	// Otherwise just extract the date portion
	return dateString.split('T')[0];
};

const EditWorkOrderModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openAddCategory } = useModal('addCategoryModal');
	const titleInputRef = useRef(null);

	const [form, setForm] = useState({
		title: '',
		description: '',
		categoryId: null,
		assignedTo: null,
		scheduledStartDate: '',
		scheduledEndDate: '',
		allDay: true,
		startTime: '09:00',
		endTime: '17:00',
		sopId: null,
		sopVersionId: null, // null = always use latest
		requireSopCompletion: false,
		serviceReportRequired: false,
		contractId: null,
		vendorId: null,
		assets: [],
		contacts: [],
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [originalAssets, setOriginalAssets] = useState([]);
	const [originalContacts, setOriginalContacts] = useState([]);
	const [categories, setCategories] = useState([]);
	const [originalSopId, setOriginalSopId] = useState(null);
	const [sopQuery, setSopQuery] = useState('');
	const [sopVersions, setSopVersions] = useState([]);
	const [loadingVersions, setLoadingVersions] = useState(false);
	const sopComboboxButtonRef = useRef(null);

	const isEditing = !!open?.workOrder?.id;

	// Data fetching
	const { value: team } = useAsync(getTeam);
	const { value: assets } = useAsync(getAssets, {
		defaultPayload: { pageSize: null },
	});
	const { value: sops, execute: refreshSops } = useAsync(getApprovedSops, {
		defaultPayload: {},
	});
	const { value: contacts } = useAsync(getContacts, {
		defaultPayload: { pageSize: null },
	});
	const { value: contracts } = useAsync(getContracts, {
		defaultPayload: { status: 'active', pageSize: null },
	});

	// Process contracts with selected asset counts
	const processedContracts = useMemo(() => {
		if (!contracts?.rows) return [];
		const selectedAssetIds = form.assets.map((a) => a.assetId);
		const totalSelected = selectedAssetIds.length;

		return contracts.rows
			.filter((contract) => contract.active)
			.map((contract) => {
				const contractAssetIds = (contract.assets || []).map((a) => a.id);
				const selectedInContract = selectedAssetIds.filter((id) =>
					contractAssetIds.includes(id),
				).length;

				return {
					id: contract.id,
					name: contract.name,
					assets: contract.assets || [],
					selectedInContract,
					totalSelected,
				};
			});
	}, [contracts, form.assets]);

	// Process vendors with selected asset counts
	const processedVendors = useMemo(() => {
		if (!contacts?.rows || !assets?.rows) return [];
		const selectedAssetIds = form.assets.map((a) => a.assetId);
		const totalSelected = selectedAssetIds.length;

		const vendors = contacts.rows;

		return vendors
			.map((vendor) => {
				let selectedWithVendor = 0;
				selectedAssetIds.forEach((assetId) => {
					const asset = assets.rows.find((a) => a.id === assetId);
					if (asset?.contracts) {
						const hasVendor = asset.contracts.some((contract) =>
							contract.contacts?.some((c) => c.id === vendor.id),
						);
						if (hasVendor) selectedWithVendor++;
					}
				});

				const isCompany = vendor.type === 'company' || !!vendor.companyName;
				const displayName = isCompany
					? vendor.companyName || vendor.name
					: `${vendor.firstName || ''} ${vendor.lastName || ''}`.trim() ||
						vendor.name;

				return {
					id: vendor.id,
					name: displayName,
					isCompany,
					email: vendor.email,
					selectedWithVendor,
					totalSelected,
				};
			})
			.sort((a, b) => {
				if (b.selectedWithVendor !== a.selectedWithVendor) {
					return b.selectedWithVendor - a.selectedWithVendor;
				}
				return a.name.localeCompare(b.name);
			});
	}, [contacts, assets, form.assets]);

	// Process assets data for selector
	const processedAssets = useMemo(() => {
		if (!assets?.rows) return [];
		return assets.rows.map((asset) => ({
			id: asset.id,
			name:
				[
					asset.manufacturer?.name || asset.manufacturerName,
					asset.model?.name || asset.modelName,
				]
					.filter(Boolean)
					.join(' ') || 'Unnamed Asset',
			manufacturer:
				asset.manufacturer?.name || asset.manufacturerName || 'Unknown',
			model: asset.model?.name || asset.modelName || '',
			type: asset.model?.name || asset.modelName || 'Unknown Type',
			location: asset.location?.name || asset.locationName || 'No Location',
			serial: asset.serialNumber || 'N/A',
		}));
	}, [assets]);

	// Filter and sort SOPs based on search query
	const filteredSops = useMemo(() => {
		const sopList = sops?.rows || sops || [];
		const sorted = [...sopList].sort((a, b) =>
			(a.name || '').localeCompare(b.name || '', undefined, {
				numeric: true,
				sensitivity: 'base',
			}),
		);
		if (!sopQuery.trim()) return sorted;
		const query = sopQuery.toLowerCase();
		return sorted.filter(
			(sop) =>
				sop.name?.toLowerCase().includes(query) ||
				sop.description?.toLowerCase().includes(query),
		);
	}, [sops, sopQuery]);

	// Selected SOP for display
	const selectedSop = useMemo(() => {
		const sopList = sops?.rows || sops || [];
		return sopList.find((s) => s.id === form.sopId);
	}, [sops, form.sopId]);

	// Fetch SOP versions when SOP is selected
	useEffect(() => {
		if (form.sopId) {
			setLoadingVersions(true);
			setSopVersions([]);
			getSopVersions({ id: form.sopId })
				.then((versions) => {
					setSopVersions(versions || []);
				})
				.catch((err) => {
					console.error('Failed to load SOP versions:', err);
					setSopVersions([]);
				})
				.finally(() => {
					setLoadingVersions(false);
				});
		} else {
			setSopVersions([]);
		}
	}, [form.sopId]);

	// Fetch categories
	const fetchCategories = async () => {
		try {
			const result = await getModuleCategoriesByModuleKey({
				key: 'default:work-order',
			});
			setCategories(result || []);
		} catch (error) {
			console.error('Failed to fetch categories:', error);
			setCategories([]);
		}
	};

	useEffect(() => {
		fetchCategories();
	}, []);

	useEffect(() => {
		if (open?.workOrder) {
			const workOrder = open.workOrder;
			// Transform existing assets to our format
			const assetData =
				workOrder.assets?.map((a) => ({
					assetId: a.id,
					contractId: a.work_order_asset?.contractId || null,
				})) || [];

			// Get contact IDs
			const contactIds = workOrder.contacts?.map((c) => c.id) || [];

			// Parse times from scheduled dates if not all-day
			let startTime = '09:00';
			let endTime = '17:00';
			if (!workOrder.allDay && workOrder.scheduledStartDate) {
				const startDate = new Date(workOrder.scheduledStartDate);
				startTime = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`;
			}
			if (!workOrder.allDay && workOrder.scheduledEndDate) {
				const endDate = new Date(workOrder.scheduledEndDate);
				endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
			}

			const sopId = workOrder.sopId || workOrder.sop?.id || null;
			setForm({
				title: workOrder.title || '',
				description: workOrder.description || '',
				categoryId: workOrder.categoryId || null,
				assignedTo: workOrder.assignedTo || null,
				scheduledStartDate: workOrder.scheduledStartDate || '',
				scheduledEndDate: workOrder.scheduledEndDate || '',
				allDay: workOrder.allDay !== undefined ? workOrder.allDay : true,
				startTime,
				endTime,
				sopId,
				sopVersionId: workOrder.sopVersionId || null,
				requireSopCompletion: workOrder.requireSopCompletion || false,
				serviceReportRequired: workOrder.serviceReportRequired || false,
				contractId: workOrder.contractId || null,
				vendorId: workOrder.vendorId || null,
				assets: assetData,
				contacts: contactIds,
			});
			setOriginalAssets(assetData);
			setOriginalContacts(contactIds);
			setOriginalSopId(sopId);
			refreshSops({});
		} else if (open) {
			setForm({
				title: '',
				description: '',
				categoryId: null,
				assignedTo: null,
				scheduledStartDate: '',
				scheduledEndDate: '',
				allDay: true,
				startTime: '09:00',
				endTime: '17:00',
				sopId: null,
				sopVersionId: null,
				requireSopCompletion: false,
				serviceReportRequired: false,
				contractId: null,
				vendorId: null,
				assets: [],
				contacts: [],
			});
			setOriginalAssets([]);
			setOriginalContacts([]);
			refreshSops({});
		}
	}, [open]);

	const handleClose = () => {
		setForm({
			title: '',
			description: '',
			categoryId: null,
			assignedTo: null,
			scheduledStartDate: '',
			scheduledEndDate: '',
			allDay: true,
			startTime: '09:00',
			endTime: '17:00',
			sopId: null,
			sopVersionId: null,
			requireSopCompletion: false,
			serviceReportRequired: false,
			contractId: null,
			vendorId: null,
			assets: [],
			contacts: [],
		});
		setShowDeleteConfirm(false);
		setOriginalAssets([]);
		setOriginalContacts([]);
		setOriginalSopId(null);
		setClose();
		if (typeof open?.onClose === 'function') {
			open.onClose();
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!form.title.trim()) return;

		setIsSubmitting(true);

		try {
			// Build scheduled dates with times if not all-day
			let scheduledStartDate = form.scheduledStartDate || null;
			let scheduledEndDate = form.scheduledEndDate || null;

			if (!form.allDay && scheduledStartDate && scheduledEndDate) {
				// Extract just the date portions (YYYY-MM-DD), converting UTC to local if needed
				const startDateOnly = getLocalDateString(scheduledStartDate);
				const endDateOnly = getLocalDateString(scheduledEndDate);
				// Combine with times
				scheduledStartDate = `${startDateOnly}T${form.startTime}:00`;
				scheduledEndDate = `${endDateOnly}T${form.endTime}:00`;
			}

			const payload = {
				title: form.title,
				description: form.description,
				categoryId: form.categoryId,
				scheduledStartDate,
				scheduledEndDate,
				allDay: form.allDay,
				assignedTo: form.assignedTo,
				sopId: form.sopId,
				sopVersionId: form.sopId ? form.sopVersionId : null,
				requireSopCompletion: form.sopId ? form.requireSopCompletion : false,
				serviceReportRequired: form.serviceReportRequired,
				contractId: form.contractId,
				vendorId: form.vendorId,
			};

			await updateWorkOrder({ id: open.workOrder.id, ...payload });

			// Handle asset changes
			const originalIds = originalAssets.map((a) => a.assetId);
			const currentIds = form.assets.map((a) => a.assetId);

			// Remove assets that were removed
			for (const assetId of originalIds) {
				if (!currentIds.includes(assetId)) {
					await removeWorkOrderAsset({ id: open.workOrder.id, assetId });
				}
			}

			// Add new assets
			for (const asset of form.assets) {
				if (!originalIds.includes(asset.assetId)) {
					await addWorkOrderAsset({
						id: open.workOrder.id,
						assetId: asset.assetId,
						contractId: asset.contractId || null,
					});
				}
			}

			// Handle contact changes
			const currentContactIds = form.contacts;

			// Remove contacts that were removed
			for (const contactId of originalContacts) {
				if (!currentContactIds.includes(contactId)) {
					await removeWorkOrderContact({ id: open.workOrder.id, contactId });
				}
			}

			// Add new contacts
			for (const contactId of currentContactIds) {
				if (!originalContacts.includes(contactId)) {
					await addWorkOrderContact({
						id: open.workOrder.id,
						contactId,
					});
				}
			}

			if (typeof open?.onSuccess === 'function') {
				open.onSuccess();
			}
			handleClose();
		} catch (error) {
			console.error('Failed to save Work Order:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDelete = async () => {
		if (!isEditing) return;

		setIsSubmitting(true);
		try {
			await deleteWorkOrder({ id: open.workOrder.id });
			if (typeof open?.onDeleted === 'function') {
				open.onDeleted();
			} else if (typeof open?.onSuccess === 'function') {
				open.onSuccess();
			}
			handleClose();
		} catch (error) {
			console.error('Failed to delete Work Order:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<StackableModal
			open={!!open}
			onClose={handleClose}
			title="Edit Work Order"
			size="4xl"
			initialFocusRef={titleInputRef}
		>
			<form onSubmit={handleSubmit}>
				<div className="grid grid-cols-2 gap-6">
					{/* Left Column: Details */}
					<div className="col-span-1">
						<div className="space-y-4">
							{/* Title */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Title <span className="text-red-500">*</span>
								</label>
								<input
									ref={titleInputRef}
									type="text"
									value={form.title}
									onChange={(e) => setForm({ ...form, title: e.target.value })}
									placeholder="Enter work order title"
									className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
									required
								/>
							</div>

							{/* Description */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Description
								</label>
								<textarea
									value={form.description}
									onChange={(e) =>
										setForm({ ...form, description: e.target.value })
									}
									placeholder="Enter description..."
									rows={3}
									className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
								/>
							</div>

							<div className="grid grid-cols-2 gap-4">
								{/* Date Range */}
								<div className="col-span-2">
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Service Period
									</label>
									<DateTimePicker
										mode="range"
										startDate={form.scheduledStartDate}
										endDate={form.scheduledEndDate}
										onChange={(startDate, endDate) => {
											setForm({
												...form,
												scheduledStartDate: startDate,
												scheduledEndDate: endDate,
												status: startDate ? 'scheduled' : 'draft',
												allDay: true,
											});
										}}
										placeholder="Select date range"
										showTimeToggle={false}
										defaultAllDay={true}
										allDay={form.allDay}
									/>

									{/* All Day / Timed Toggle */}
									{form?.scheduledStartDate && (
										<>
											<div className="mt-3 flex items-center mb-4">
												<div className="flex items-center gap-2">
													<ClockIcon className="h-4 w-4 text-gray-400" />
													<span className="text-sm text-gray-700">
														All day event
													</span>
												</div>
												<button
													type="button"
													onClick={() => {
														const newAllDay = !form.allDay;
														if (newAllDay) {
															// Reset times to defaults and strip time from dates when switching to all-day
															const startDateOnly = getLocalDateString(form.scheduledStartDate);
															const endDateOnly = getLocalDateString(form.scheduledEndDate);
															setForm({
																...form,
																allDay: true,
																startTime: '09:00',
																endTime: '17:00',
																scheduledStartDate: startDateOnly,
																scheduledEndDate: endDateOnly,
															});
														} else {
															setForm({ ...form, allDay: false });
														}
													}}
													className={classNames(
														'relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
														!form.allDay ? 'bg-gray-200' : 'bg-indigo-600',
													)}
												>
													<span
														className={classNames(
															'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
															!form.allDay ? 'translate-x-0' : 'translate-x-5',
														)}
													/>
												</button>
											</div>

											{/* Time Pickers (shown when not all-day) */}
											{!form.allDay && (
												<div className="mt-3 grid grid-cols-2 gap-4">
													<div>
														<label className="block text-xs font-medium text-gray-500 mb-1">
															Start Time
														</label>
														<select
															value={form.startTime}
															onChange={(e) => {
																const newStartTime = e.target.value;
																// If end time is same or earlier, set it to 30 mins after start
																const startMinutes =
																	parseInt(newStartTime.split(':')[0]) * 60 +
																	parseInt(newStartTime.split(':')[1]);
																const endMinutes =
																	parseInt(form.endTime.split(':')[0]) * 60 +
																	parseInt(form.endTime.split(':')[1]);

																// Get date portions (convert UTC to local)
																const startDateOnly = getLocalDateString(form.scheduledStartDate);
																const endDateOnly = getLocalDateString(form.scheduledEndDate);

																if (endMinutes <= startMinutes) {
																	const newEndMinutes = startMinutes + 30;
																	const endHours =
																		Math.floor(newEndMinutes / 60) % 24;
																	const endMins = newEndMinutes % 60;
																	const newEndTime = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
																	setForm({
																		...form,
																		startTime: newStartTime,
																		endTime: newEndTime,
																		scheduledStartDate: startDateOnly
																			? `${startDateOnly}T${newStartTime}:00`
																			: '',
																		scheduledEndDate: endDateOnly
																			? `${endDateOnly}T${newEndTime}:00`
																			: '',
																	});
																} else {
																	setForm({
																		...form,
																		startTime: newStartTime,
																		scheduledStartDate: startDateOnly
																			? `${startDateOnly}T${newStartTime}:00`
																			: '',
																	});
																}
															}}
															className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
														>
															{Array.from({ length: 48 }, (_, i) => {
																const hours = Math.floor(i / 2);
																const minutes = i % 2 === 0 ? '00' : '30';
																const time = `${String(hours).padStart(2, '0')}:${minutes}`;
																const displayTime = `${hours === 0 ? 12 : hours > 12 ? hours - 12 : hours}:${minutes} ${hours < 12 ? 'AM' : 'PM'}`;
																return (
																	<option key={time} value={time}>
																		{displayTime}
																	</option>
																);
															})}
														</select>
													</div>
													<div>
														<label className="block text-xs font-medium text-gray-500 mb-1">
															End Time
														</label>
														<select
															value={form.endTime}
															onChange={(e) => {
																const newEndTime = e.target.value;
																// If end time is before start time, set start to 30 mins before end
																const startMinutes =
																	parseInt(form.startTime.split(':')[0]) * 60 +
																	parseInt(form.startTime.split(':')[1]);
																const endMinutes =
																	parseInt(newEndTime.split(':')[0]) * 60 +
																	parseInt(newEndTime.split(':')[1]);

																// Get date portions (convert UTC to local)
																const startDateOnly = getLocalDateString(form.scheduledStartDate);
																const endDateOnly = getLocalDateString(form.scheduledEndDate);

																if (endMinutes <= startMinutes) {
																	const newStartMinutes = Math.max(
																		0,
																		endMinutes - 30,
																	);
																	const startHours = Math.floor(
																		newStartMinutes / 60,
																	);
																	const startMins = newStartMinutes % 60;
																	const newStartTime = `${String(startHours).padStart(2, '0')}:${String(startMins).padStart(2, '0')}`;
																	setForm({
																		...form,
																		startTime: newStartTime,
																		endTime: newEndTime,
																		scheduledStartDate: startDateOnly
																			? `${startDateOnly}T${newStartTime}:00`
																			: '',
																		scheduledEndDate: endDateOnly
																			? `${endDateOnly}T${newEndTime}:00`
																			: '',
																	});
																} else {
																	setForm({
																		...form,
																		endTime: newEndTime,
																		scheduledEndDate: endDateOnly
																			? `${endDateOnly}T${newEndTime}:00`
																			: '',
																	});
																}
															}}
															className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
														>
															{Array.from({ length: 48 }, (_, i) => {
																const hours = Math.floor(i / 2);
																const minutes = i % 2 === 0 ? '00' : '30';
																const time = `${String(hours).padStart(2, '0')}:${minutes}`;
																const displayTime = `${hours === 0 ? 12 : hours > 12 ? hours - 12 : hours}:${minutes} ${hours < 12 ? 'AM' : 'PM'}`;
																return (
																	<option key={time} value={time}>
																		{displayTime}
																	</option>
																);
															})}
														</select>
													</div>
												</div>
											)}
										</>
									)}
								</div>

								{/*<div className="col-span-1">
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Due By
									</label>
									<DateTimePicker
										value={form.dueDate}
										startDate={form.scheduledStartDate}
										endDate={form.scheduledEndDate}
										onChange={(dueDate) => {
											setForm({
												...form,
												dueDate,
												status:
													form?.status !== 'draft'
														? form?.status
														: dueDate
															? 'pending'
															: 'draft',
											});
										}}
										placeholder="Due by date"
										showTimeToggle={false}
										defaultAllDay={true}
									/>
								</div>*/}
							</div>

							{/* Category and Assign To Row */}
							<div className="grid grid-cols-2 gap-4">
								{/* Category */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Category
									</label>
									<Listbox
										value={form.categoryId}
										onChange={(val) => setForm({ ...form, categoryId: val })}
									>
										<div className="relative">
											<Listbox.Button
												type="button"
												className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
											>
												<span>
													{form.categoryId
														? categories?.find((c) => c.id === form.categoryId)
																?.name
														: 'Select category'}
												</span>
												<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
													<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
												</span>
											</Listbox.Button>
											<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
												<Listbox.Option
													value={null}
													className="relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-500"
												>
													No category
												</Listbox.Option>
												{[...(categories || [])]
													.sort((a, b) =>
														(a.name || '').localeCompare(
															b.name || '',
															undefined,
															{
																numeric: true,
																sensitivity: 'base',
															},
														),
													)
													.map((category) => (
														<Listbox.Option
															key={category.id}
															value={category.id}
															className={({ active }) =>
																classNames(
																	active ? 'bg-indigo-50' : '',
																	'relative cursor-pointer select-none py-2 pl-3 pr-9',
																)
															}
														>
															{({ selected }) => (
																<>
																	<span>{category.name}</span>
																	{selected && (
																		<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
																			<CheckIcon className="h-5 w-5" />
																		</span>
																	)}
																</>
															)}
														</Listbox.Option>
													))}
												<div className="border-t border-gray-100 mt-1 pt-1">
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation();
															openAddCategory({
																moduleKey: 'default:work-order',
																onSubmit: (category) => {
																	setForm((prev) => ({
																		...prev,
																		categoryId: category?.value,
																	}));
																	fetchCategories();
																},
															});
														}}
														className="w-full flex items-center gap-2 px-3 py-2 text-xs text-indigo-600 hover:bg-indigo-50 font-medium"
													>
														<PlusIcon className="h-4 w-4" />
														Add category
													</button>
												</div>
											</Listbox.Options>
										</div>
									</Listbox>
								</div>

								{/* Assign To */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Assign To
									</label>
									<Listbox
										value={form.assignedTo}
										onChange={(val) => setForm({ ...form, assignedTo: val })}
									>
										<div className="relative">
											<Listbox.Button
												type="button"
												className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
											>
												<span>
													{form.assignedTo
														? getUserFullName(
																team?.rows?.find(
																	(u) => u.id === form.assignedTo,
																),
															)
														: 'Select team member'}
												</span>
												<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
													<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
												</span>
											</Listbox.Button>
											<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
												<Listbox.Option
													value={null}
													className="relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-500"
												>
													Unassigned
												</Listbox.Option>
												{team?.rows?.map((user) => (
													<Listbox.Option
														key={user.id}
														value={user.id}
														className={({ active }) =>
															classNames(
																active ? 'bg-indigo-50' : '',
																'relative cursor-pointer select-none py-2 pl-3 pr-9',
															)
														}
													>
														{({ selected }) => (
															<>
																<span>{getUserFullName(user)}</span>
																{selected && (
																	<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
																		<CheckIcon className="h-5 w-5" />
																	</span>
																)}
															</>
														)}
													</Listbox.Option>
												))}
											</Listbox.Options>
										</div>
									</Listbox>
								</div>
							</div>

							{/* SOP Template */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									SOP Template
								</label>
								<Combobox
									value={form.sopId}
									onChange={(value) => {
										setForm({ ...form, sopId: value });
										setSopQuery('');
									}}
								>
									<div className="relative">
										<div className="relative w-full cursor-default overflow-hidden rounded-md border border-gray-300 bg-white text-left focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500">
											<MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
											<Combobox.Input
												className="w-full border-none py-2 pl-9 pr-10 text-sm leading-5 text-gray-900 focus:ring-0 focus:outline-none"
												placeholder={selectedSop?.name || 'Search SOPs...'}
												displayValue={() => (sopQuery ? sopQuery : '')}
												onChange={(e) => setSopQuery(e.target.value)}
												onFocus={(e) => {
													if (selectedSop) {
														e.target.placeholder = selectedSop.name;
													}
													sopComboboxButtonRef.current?.click();
												}}
											/>
											<Combobox.Button
												ref={sopComboboxButtonRef}
												className="absolute inset-y-0 right-0 flex items-center pr-2"
											>
												<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
											</Combobox.Button>
										</div>
										<Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
											{selectedSop && (
												<Combobox.Option
													value={null}
													className={({ active }) =>
														classNames(
															'cursor-pointer select-none relative py-2 pl-3 pr-9',
															active ? 'bg-indigo-50' : '',
														)
													}
												>
													<span className="text-gray-500">Clear selection</span>
												</Combobox.Option>
											)}
											{filteredSops.length === 0 && sopQuery !== '' ? (
												<div className="relative cursor-default select-none py-2 px-4 text-gray-500">
													No SOPs found.
												</div>
											) : (
												filteredSops.map((sop) => (
													<Combobox.Option
														key={sop.id}
														value={sop.id}
														className={({ active, selected }) =>
															classNames(
																'cursor-pointer select-none relative py-2 pl-3 pr-9',
																active ? 'bg-indigo-50' : '',
																selected ? 'bg-indigo-100' : '',
															)
														}
													>
														{({ selected }) => (
															<div>
																<div className="flex items-center justify-between">
																	<span
																		className={selected ? 'font-semibold' : ''}
																	>
																		{sop.name}
																	</span>
																	{selected && (
																		<CheckIcon className="h-4 w-4 text-indigo-600" />
																	)}
																</div>
																<p className="text-xs text-gray-500 mt-0.5">
																	{sop.versionMajor !== undefined &&
																	sop.versionMinor !== undefined
																		? `Latest: v${sop.versionMajor}.${sop.versionMinor}`
																		: sop.steps?.length > 0
																			? `${sop.steps.length} step${sop.steps.length !== 1 ? 's' : ''}`
																			: 'Unpublished'}
																</p>
															</div>
														)}
													</Combobox.Option>
												))
											)}
										</Combobox.Options>
									</div>
								</Combobox>

								{/* SOP Version Selection and Require Completion - only show when SOP is selected */}
								{form.sopId && (
									<>
										<div className="mt-2">
											<label className="block text-xs text-gray-500 mb-1">
												SOP Version
											</label>
											<Listbox
												value={form.sopVersionId}
												onChange={(val) =>
													setForm({ ...form, sopVersionId: val })
												}
											>
												<div className="relative">
													<Listbox.Button
														type="button"
														className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
													>
														{loadingVersions ? (
															<span className="text-gray-400">
																Loading versions...
															</span>
														) : form.sopVersionId ? (
															<span>
																Version{' '}
																{
																	sopVersions.find(
																		(v) => v.id === form.sopVersionId,
																	)?.versionMajor
																}
																.
																{
																	sopVersions.find(
																		(v) => v.id === form.sopVersionId,
																	)?.versionMinor
																}
															</span>
														) : (
															<span className="text-gray-700">
																Always use latest version
															</span>
														)}
														<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
															<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
														</span>
													</Listbox.Button>
													<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
														<Listbox.Option
															value={null}
															className={({ active, selected }) =>
																classNames(
																	active ? 'bg-indigo-50' : '',
																	selected ? 'bg-indigo-100' : '',
																	'relative cursor-pointer select-none py-2 pl-3 pr-9',
																)
															}
														>
															{({ selected }) => (
																<div className="flex items-center justify-between">
																	<div>
																		<span
																			className={selected ? 'font-semibold' : ''}
																		>
																			Always use latest version
																		</span>
																		<p className="text-xs text-gray-500">
																			Work order will always use the most recent
																			SOP version
																		</p>
																	</div>
																	{selected && (
																		<CheckIcon className="h-4 w-4 text-indigo-600" />
																	)}
																</div>
															)}
														</Listbox.Option>
														{sopVersions.map((version) => (
															<Listbox.Option
																key={version.id}
																value={version.id}
																className={({ active, selected }) =>
																	classNames(
																		active ? 'bg-indigo-50' : '',
																		selected ? 'bg-indigo-100' : '',
																		'relative cursor-pointer select-none py-2 pl-3 pr-9',
																	)
																}
															>
																{({ selected }) => (
																	<div className="flex items-center justify-between">
																		<div>
																			<span
																				className={
																					selected ? 'font-semibold' : ''
																				}
																			>
																				Version {version.versionMajor}.
																				{version.versionMinor}
																			</span>
																			{version.publishedAt && (
																				<p className="text-xs text-gray-500">
																					Published{' '}
																					{new Date(
																						version.publishedAt,
																					).toLocaleDateString()}
																				</p>
																			)}
																		</div>
																		{selected && (
																			<CheckIcon className="h-4 w-4 text-indigo-600" />
																		)}
																	</div>
																)}
															</Listbox.Option>
														))}
													</Listbox.Options>
												</div>
											</Listbox>
										</div>

										{/* Require SOP Completion checkbox */}
										<div className="mt-3">
											<label className="flex items-center gap-2 cursor-pointer">
												<input
													type="checkbox"
													checked={form.requireSopCompletion}
													onChange={(e) =>
														setForm({
															...form,
															requireSopCompletion: e.target.checked,
														})
													}
													className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
												/>
												<span className="text-sm text-gray-700 ml-1.5">
													Require SOP completion before work order can be
													completed
												</span>
											</label>
										</div>
									</>
								)}

								{isEditing && originalSopId && form.sopId !== originalSopId && (
									<div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
										<p className="text-xs text-amber-800 font-medium">
											Warning: Changing the SOP will remove all existing SOP
											checklist items and their data.
										</p>
									</div>
								)}
							</div>

							{/* Service Report Required */}
							<div className="flex items-center gap-3 pt-2">
								<input
									type="checkbox"
									id="serviceReportRequired"
									checked={form.serviceReportRequired}
									onChange={(e) =>
										setForm({
											...form,
											serviceReportRequired: e.target.checked,
										})
									}
									className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
								/>
								<div>
									<label
										htmlFor="serviceReportRequired"
										className="text-sm font-medium text-gray-700"
									>
										Service Report Required
									</label>
									<p className="text-xs text-gray-500">
										A service report must be uploaded before the work order can be completed
									</p>
								</div>
							</div>

							{/* Delete Confirmation */}
							{showDeleteConfirm && (
								<div className="bg-red-50 border border-red-200 rounded-lg p-4">
									<p className="text-sm text-red-700 mb-3">
										Are you sure you want to delete this work order? This action
										cannot be undone.
									</p>
									<div className="flex gap-2 mt-3">
										<button
											type="button"
											onClick={handleDelete}
											disabled={isSubmitting}
											className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
										>
											{isSubmitting ? 'Deleting...' : 'Delete'}
										</button>
										<button
											type="button"
											onClick={() => setShowDeleteConfirm(false)}
											className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
										>
											Cancel
										</button>
									</div>
								</div>
							)}
						</div>
					</div>

					{/* Right Column: Assets, Contract, Vendor */}
					<div className="col-span-1">
						<div className="space-y-4">
							{/* Assets */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Assets
								</label>
								<p className="text-xs text-gray-500 mb-3">
									Select assets for this work order
								</p>

								<AssetSelectorBeta
									value={form.assets.map((a) => a.assetId)}
									onChange={(assetIds) => {
										setForm((prev) => ({
											...prev,
											assets: assetIds.map((assetId) => {
												const existing = prev.assets.find(
													(a) => a.assetId === assetId,
												);
												return existing || { assetId, contractId: null };
											}),
										}));
									}}
									assets={processedAssets}
									placeholder="Select assets..."
									multiple={true}
									allowGroups={false}
								/>
							</div>

							{/* Contract Selection */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Contract
								</label>
								<Listbox
									value={form.contractId}
									onChange={(val) => setForm({ ...form, contractId: val })}
								>
									<div className="relative">
										<Listbox.Button
											type="button"
											className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
										>
											<span>
												{form.contractId
													? processedContracts?.find(
															(c) => c.id === form.contractId,
														)?.name || 'Selected contract'
													: 'Select contract (optional)'}
											</span>
											<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
												<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
											</span>
										</Listbox.Button>
										<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
											<Listbox.Option
												value={null}
												className="relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-500"
											>
												No contract
											</Listbox.Option>
											{processedContracts?.map((contract) => (
												<Listbox.Option
													key={contract.id}
													value={contract.id}
													className={({ active }) =>
														classNames(
															active ? 'bg-indigo-50' : '',
															'relative cursor-pointer select-none py-2 pl-3 pr-9',
														)
													}
												>
													{({ selected }) => (
														<>
															<div>
																<span className="font-medium">
																	{contract.name}
																</span>
																{contract.totalSelected > 0 && (
																	<p className="text-xs text-gray-500 mt-0.5">
																		{contract.selectedInContract} out of{' '}
																		{contract.totalSelected} selected asset
																		{contract.totalSelected !== 1 ? 's' : ''} in
																		contract
																	</p>
																)}
															</div>
															{selected && (
																<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
																	<CheckIcon className="h-5 w-5" />
																</span>
															)}
														</>
													)}
												</Listbox.Option>
											))}
										</Listbox.Options>
									</div>
								</Listbox>
							</div>

							{/* Vendor Selection */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Vendors
								</label>
								<VendorSelectorBeta
									value={form.contacts}
									onChange={(vendorIds) =>
										setForm({ ...form, contacts: vendorIds })
									}
									vendors={processedVendors}
									placeholder="Select vendors..."
									multiple={true}
								/>
							</div>
						</div>
					</div>
				</div>

				{/* Actions */}
				<div className="flex justify-between gap-3 pt-6 mt-6">
					<div>
						{isEditing && !showDeleteConfirm && (
							<button
								type="button"
								onClick={() => setShowDeleteConfirm(true)}
								className="text-sm text-red-600 hover:text-red-700"
							>
								Delete
							</button>
						)}
					</div>
					<div className="flex gap-3">
						<button
							type="button"
							onClick={handleClose}
							className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={!form.title.trim() || isSubmitting}
							className={classNames(
								'inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm',
								!form.title.trim() || isSubmitting
									? 'bg-gray-400 cursor-not-allowed'
									: 'bg-indigo-600 hover:bg-indigo-700',
							)}
						>
							{isSubmitting ? 'Saving...' : 'Save Changes'}
						</button>
					</div>
				</div>
			</form>
		</StackableModal>
	);
};

export default EditWorkOrderModal;
